// Centralized GraphQL fetch function
const SHOPIFY_STOREFRONT_API_URL = 'https://gsmnet.eu/api/2025-01/graphql.json';
const STOREFRONT_ACCESS_TOKEN = 'bcad781e786bbe15aa1bebada86254b6';

async function fetchGraphQL(query, variables = {}, language = null) {
  const headers = {
    'Content-Type': 'application/json',
    'X-Shopify-Storefront-Access-Token': STOREFRONT_ACCESS_TOKEN

  };
  if (language) {
    headers['Accept-Language'] = language;
  }

  try {
    const response = await fetch(SHOPIFY_STOREFRONT_API_URL, {
      method: 'POST',
      headers,
      body: JSON.stringify({ query, variables })
    });

    const result = await response.json();
    if (result.errors) {
      console.error('GraphQL Errors:', result.errors);
      throw new Error('GraphQL query failed');
    }
    return result.data;
  } catch (error) {
    console.error('Fetch Error:', error);
    throw error;
  }
}

// Utility to parse JSON safely and ensure an array output
function parseJsonSafe(value, fallback = []) {
  if (!value) return fallback; // Handle undefined or null

  try {
    const parsed = JSON.parse(value);
    // Normalize to array: if it's already an array, return it; otherwise, wrap it in an array
    return Array.isArray(parsed) ? parsed : [parsed];
  } catch (error) {
    console.error('Error parsing JSON:', error, value);

    // If parsing fails but value is a valid string, return it as an array
    return typeof value === 'string' && value.trim() ? [value.trim()] : fallback;
  }
}

// Fetch all unique brands and their models
async function fetchAllProductsBrandsAndModels(collectionId) {
  const brandModelMap = new Map();
  let cursor = null;

  const query = `
    query Products($id: ID!, $cursor: String) {
      collection(id: $id) {
        products(first: 250, after: $cursor, sortKey: ID) {
          edges {
            node {
              publishedAt
              brand: metafield(namespace: "is_hidden", key: "marca_dispozitiv") {
                value
              }
              models: metafield(namespace: "is_hidden", key: "model_dispozitiv") {
                value
              }
            }
          }
          pageInfo {
            hasNextPage
            endCursor
          }
        }
      }
    }
  `;

  while (true) {
    const variables = cursor ? { id: collectionId, cursor } : { id: collectionId };
    const data = await fetchGraphQL(query, variables);

    if (!data?.collection?.products?.edges.length) {
      console.warn('No more products found or invalid collection ID.');
      break;
    }

    data.collection.products.edges
      .filter(edge => edge.node.publishedAt)
      .forEach(edge => {
        const brand = parseJsonSafe(edge.node.brand?.value, null);
        const brandName = Array.isArray(brand) ? brand[0]?.trim() : brand?.trim();
        const models = parseJsonSafe(edge.node.models?.value, []);

        if (brandName && models.length > 0) {
          if (!brandModelMap.has(brandName)) {
            brandModelMap.set(brandName, new Set());
          }
          models.forEach(model => brandModelMap.get(brandName).add(model));
        }
      });

    const pageInfo = data.collection.products.pageInfo;
    if (!pageInfo.hasNextPage) break;

    cursor = pageInfo.endCursor;
    await new Promise(resolve => setTimeout(resolve, 750));
  }

  const aggregatedProducts = Array.from(brandModelMap.entries()).map(([brand, modelsSet]) => ({
    brand,
    models: Array.from(modelsSet).sort()
  }));

  return aggregatedProducts;
}


async function fetchAllProductsModels(collectionHandle) {
  let uniqueModels = new Set();
  let cursor = null;

  const query = `
    query Products($cursor: String) {
      collectionByHandle(handle: "${collectionHandle}") {
        products(first: 250, after: $cursor, sortKey: ID) {
          edges {
            node {
              publishedAt
              metafield(namespace: "is_hidden", key: "model_dispozitiv") {
                value
              }
            }
          }
          pageInfo {
            hasNextPage
            endCursor
          }
        }
      }
    }
  `;

  while (true) {
    const variables = cursor ? { cursor } : {};
    const data = await fetchGraphQL(query, variables);

    if (!data?.collectionByHandle?.products?.edges.length) {
      console.warn('No more products found.');
      break;
    }

    const collection = data.collectionByHandle;
    collection.products.edges.forEach(edge => {
      if (!edge.node.publishedAt) return;

      const models = edge.node.metafield?.value;
      if (models) {
        const modelArray = parseJsonSafe(models);
        modelArray.forEach(model => uniqueModels.add(model.trim()));
      }
    });

    const pageInfo = collection.products.pageInfo;
    if (!pageInfo.hasNextPage) break;

    cursor = pageInfo.endCursor;
    await new Promise(resolve => setTimeout(resolve, 750));
  }

  return Array.from(uniqueModels).sort();
}

async function fetchModelsByBrand(collectionHandle, collectionId, brandValue) {
  const normalizedBrand = brandValue.trim().toLowerCase();
  const cacheKey = `models_${collectionHandle}_${normalizedBrand}`;
  const cachedModels = sessionStorage.getItem(cacheKey);
  if (cachedModels) {
    return JSON.parse(cachedModels);
  }

  const query = `
    query Products($collectionId: ID!, $cursor: String) {
      collection(id: $collectionId) {
        products(first: 250, after: $cursor) {
          edges {
            node {
              brand: metafield(namespace: "is_hidden", key: "marca_dispozitiv") {
                value
              }
              models: metafield(namespace: "is_hidden", key: "model_dispozitiv") {
                value
              }
            }
          }
          pageInfo {
            hasNextPage
            endCursor
          }
        }
      }
    }
  `;

  const modelCount = new Map();
  const fetchPage = async (cursor, retries = 3) => {
    const variables = {
      collectionId: collectionId,
      ...(cursor && { cursor }) // Only add cursor if it exists
    };
    try {
      const data = await fetchGraphQL(query, variables);
      const collection = data?.collection;

      if (!collection?.products?.edges.length) return null;

      collection.products.edges.forEach(edge => {
        const brand = edge.node.brand?.value;
        const models = edge.node.models?.value;

        if (brand) {
          const brandArray = parseJsonSafe(brand);
          const matchesBrand = Array.isArray(brandArray)
            ? brandArray.some(b => b.trim().toLowerCase() === normalizedBrand)
            : brand.trim().toLowerCase() === normalizedBrand;

          if (matchesBrand && models) {
            const modelArray = parseJsonSafe(models);
            modelArray.forEach(model => {
              const modelName = model.trim();
              modelCount.set(modelName, (modelCount.get(modelName) || 0) + 1);
            });
          }
        }
      });

      return collection.products.pageInfo;
    } catch (err) {
      if (retries > 0 && err.status !== 430) {
        await new Promise(resolve => setTimeout(resolve, 500 * (4 - retries))); // Exponential backoff
        return fetchPage(cursor, retries - 1);
      }
      console.error('Fetch failed:', err);
      return null;
    }
  };

  let cursors = [null];
  const maxConcurrent = 8; // Storefront API has no strict limit
  const allPromises = [];

  while (cursors.length > 0) {
    const batch = cursors.splice(0, maxConcurrent);
    const batchPromises = batch.map(cursor => fetchPage(cursor));
    allPromises.push(...batchPromises);

    const results = await Promise.all(batchPromises);
    results.forEach(pageInfo => {
      if (pageInfo?.hasNextPage) cursors.push(pageInfo.endCursor);
    });
  }

  await Promise.all(allPromises); // Ensure all pages are processed

  const sortedModels = Array.from(modelCount.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  sessionStorage.setItem(cacheKey, JSON.stringify(sortedModels));
  return sortedModels;
}


window.vendorDataCache = window.vendorDataCache || {};

async function fetchModelAndProductTypesByVendor(vendorValue, shopLanguage) {
  if (window.vendorDataCache[vendorValue]) {
    return window.vendorDataCache[vendorValue];
  }

  async function fetchProductsByLanguage(language, vendorValue) {
    let cursor = null;
    const productTypeCount = new Map();
    const modelCount = new Map();

    const query = `
      query ProductsByVendor($cursor: String) {
        products(first: 250, query: "vendor:${vendorValue}", after: $cursor, sortKey: ID) {
          edges {
            node {
              publishedAt
              vendor
              productType
              models: metafield(namespace: "is_hidden", key: "model_dispozitiv") {
                value
              }
            }
          }
          pageInfo {
            hasNextPage
            endCursor
          }
        }
      }
    `;

    while (true) {
      const variables = cursor ? { cursor } : {};
      const data = await fetchGraphQL(query, variables, language);

      if (!data?.products?.edges.length) {
        console.warn(`No more products found for vendor (${language}):`, vendorValue);
        break;
      }

      const products = data.products;
      products.edges.forEach(edge => {
        if (!edge.node.publishedAt) return;

        const vendor = edge.node.vendor;
        const models = edge.node.models?.value;
        const productType = edge.node.productType;

        const matchesVendor = vendor.trim().toLowerCase() === vendorValue.trim().toLowerCase();

        if (matchesVendor) {
          if (models && language === shopLanguage) {
            const modelArray = parseJsonSafe(models);
            modelArray.forEach(model => {
              const modelName = model.trim();
              modelCount.set(modelName, (modelCount.get(modelName) || 0) + 1);
            });
          }

          if (productType) {
            const typeName = productType.trim();
            productTypeCount.set(typeName, (productTypeCount.get(typeName) || 0) + 1);
          }
        }
      });

      const pageInfo = products.pageInfo;
      if (!pageInfo.hasNextPage) break;

      cursor = pageInfo.endCursor;
      await new Promise(resolve => setTimeout(resolve, 750));
    }

    return { productTypeCount, modelCount };
  }

  const englishData = await fetchProductsByLanguage('en', vendorValue);
  const localData = await fetchProductsByLanguage(shopLanguage, vendorValue);

  const sortedModels = Array.from(localData.modelCount.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  const unsortedProductTypesEnglish = Array.from(englishData.productTypeCount.entries());
  const unsortedProductTypesLocal = Array.from(localData.productTypeCount.entries());

  window.vendorDataCache[vendorValue] = {
    models: sortedModels,
    productTypesEnglish: unsortedProductTypesEnglish,
    productTypesLocal: unsortedProductTypesLocal
  };

  return window.vendorDataCache[vendorValue];
}

async function fetchVariantsCollectionId(productId) {
  const query = `
    query ($productId: ID!) {
      product(id: $productId) {
        collections(first: 10) {
          edges {
            node {
              id
              title
              handle
              is_query_variants: metafield(namespace: "collection_custom", key: "query_variants") {
                value
              }
            }
          }
        }
      }
    }
  `;

  const variables = { productId: `gid://shopify/Product/${productId}` };

  try {
    const data = await fetchGraphQL(query, variables);
    if (!data?.product?.collections?.edges) {
      console.warn(`No collections found for product ID: ${productId}`);
      return null;
    }

    // Find the first collection active for query variants
    const variantsCollection = data.product.collections.edges.find(edge => {
      const is_query_variants = edge.node.is_query_variants || [];
      return parseJsonSafe(is_query_variants?.value)[0] === true;
    });

    if (!variantsCollection) {
      console.warn(`No child collection found for product ID: ${productId}`);
      return null;
    }

    return variantsCollection.node.id; // Returns full GID, e.g., "gid://shopify/Collection/123456789"
  } catch (error) {
    console.error('Failed to fetch child collection ID:', error);
    throw error;
  }
}

async function fetchProductVariants(collectionId, familyIdKeys, familyIdValues, shopLanguage) {
  const allProducts = [];
  let hasNextPage = true;
  let cursor = null;
  let pageCount = 0; // Added for better logging

  // Generate individual metafield queries for each family_id key
  const familyIdMetafieldQueries = familyIdKeys
    .map(key => `
      family_id_${key}: metafield(namespace: "family_id", key: "${key}") {
        namespace
        key
        value
      }
    `)
    .join('\n');

  const isHiddenMetafieldQueries = familyIdKeys
    .map(key => `
      is_hidden_${key}: metafield(namespace: "is_hidden", key: "${key}") {
        namespace
        key
        value
      }
    `)
    .join('\n');

  const prezentareMetafieldQueries = familyIdKeys
    .map(key => `
      prezentare_${key}: metafield(namespace: "prezentare", key: "${key}") {
        namespace
        key
        value
      }
    `)
    .join('\n');

  const query = `
    query ($collectionId: ID!, $cursor: String) {
      collection(id: $collectionId) {
        id
        title
        products(first: 250, after: $cursor, sortKey: ID) {
          edges {
            node {
              id
              handle
              title
              featuredImage {
                url(transform: { maxWidth: 300 })
              }
              ${familyIdMetafieldQueries}
              ${isHiddenMetafieldQueries}
              ${prezentareMetafieldQueries}
            }
          }
          pageInfo {
            hasNextPage
            endCursor
          }
        }
      }
    }
  `;

  while (hasNextPage) {
    const variables = { collectionId, cursor };
    try {
      pageCount++;
      const data = await fetchGraphQL(query, variables, shopLanguage);

      if (!data?.collection?.products?.edges) {
        console.warn(`No products found in collection ID: ${collectionId}`);
        return { id: collectionId, title: data?.collection?.title || 'Unknown', products: { edges: allProducts } };
      }

      // Normalize the response to match the previous metafields array structure
      const filteredProducts = data.collection.products.edges
        .map(edge => {
          const product = edge.node;
          // Convert individual metafield fields into an array
          const metafields = [
            ...familyIdKeys.map(key => {
              const metafield = product[`family_id_${key}`];
              return metafield ? { namespace: "family_id", key, value: metafield.value } : null;
            }),
            ...familyIdKeys.map(key => {
              const metafield = product[`is_hidden_${key}`];
              return metafield ? { namespace: "is_hidden", key, value: metafield.value } : null;
            }),
            ...familyIdKeys.map(key => {
              const metafield = product[`prezentare_${key}`];
              return metafield ? { namespace: "prezentare", key, value: metafield.value } : null;
            }),
          ].filter(Boolean); // Remove null entries

          return { node: { ...product, metafields } };
        })
        .filter(({ node }) => {
          const familyIdMetafields = node.metafields.filter(m => m?.namespace === 'family_id' && m?.value);
          // Existing condition: Ensure there’s at least one non-empty family_id metafield
          const hasNonEmptyFamilyId = familyIdMetafields.length > 0 && familyIdMetafields.some(m => m.value.trim() !== '');
          if (!hasNonEmptyFamilyId) return false;

          // New condition: Check if any family_id metafield value contains any value from familyIdValues
          if (familyIdValues && familyIdValues.length > 0) {
            return familyIdMetafields.some(m =>
              familyIdValues.some(value => m.value.toLowerCase().includes(value.toLowerCase()))
            );
          }

          // If familyIdValues is empty or not provided, skip this check
          return true;
        });


      allProducts.push(...filteredProducts);
      hasNextPage = data.collection.products.pageInfo.hasNextPage;
      cursor = data.collection.products.pageInfo.endCursor;
    } catch (error) {
      console.error('Failed to fetch product variants page:', error);
      throw error;
    }
  }

  return {
    id: collectionId,
    products: { edges: allProducts },
  };
}



function capitalizeFirstLetter(str) {
  return str.charAt(0).toUpperCase() + str.slice(1);
}